import { useRuntimeVariables } from 'app-kit'
import type { Axios } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

const DEBOUNCE_MS = 300

const NormalizedBBoxSchema = z.object({
  left: z.number(),
  top: z.number(),
  width: z.number(),
  height: z.number(),
})

const IntraDocSearchHitSchema = z.object({
  page_number: z.number(),
  matched_text: z.string(),
  bounding_boxes: z.array(NormalizedBBoxSchema),
})

const IntraDocSearchResponseSchema = z.object({
  query: z.string(),
  document_id: z.string(),
  total_hits: z.number(),
  hits: z.array(IntraDocSearchHitSchema),
})

export type IntraDocSearchHit = {
  page_number: number
  matched_text: string
  bounding_boxes: NormalizedBBox[]
}

/**
 * Shared API call for intra-document search. Used by both the interactive
 * Ctrl+F search and one-shot fragment highlighting.
 */
export async function fetchIntraDocSearchHits(
  axios: Pick<Axios, 'post'>,
  backendUrl: string,
  documentId: string,
  siteId: string,
  query: string,
  signal?: AbortSignal,
  limit: number = 200,
  previewMode: boolean = false
): Promise<IntraDocSearchHit[]> {
  const url = `${backendUrl}/api/v2/documents/search/intra/${documentId}`
  const res = await axios.post(
    url,
    { query, site_id: siteId, limit, preview_mode: previewMode },
    { signal }
  )
  const parsed = IntraDocSearchResponseSchema.parse(res.data)
  return parsed.hits
}

export type IntraDocumentSearchState = {
  isOpen: boolean
  query: string
  hits: IntraDocSearchHit[]
  activeHitIndex: number
  activeHit: IntraDocSearchHit | null
  isLoading: boolean
  open: () => void
  close: () => void
  setQuery: (query: string) => void
  goToNext: () => void
  goToPrevious: () => void
}

export type IntraDocSearchUiState = {
  isOpen: boolean
  query: string
  activeHitIndex: number
}

export type UseIntraDocumentSearchOptions = {
  initialState?: IntraDocSearchUiState
  onStateChange?: (state: IntraDocSearchUiState) => void
}

export function useIntraDocumentSearch(
  documentId: string,
  siteId: string,
  options?: UseIntraDocumentSearchOptions
): IntraDocumentSearchState {
  const [isOpen, setIsOpen] = useState(options?.initialState?.isOpen ?? false)
  const [query, setQuery] = useState(options?.initialState?.query ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState(
    options?.initialState?.query ?? ''
  )
  const [hits, setHits] = useState<IntraDocSearchHit[]>([])
  const [activeHitIndex, setActiveHitIndex] = useState(
    options?.initialState?.activeHitIndex ?? -1
  )
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const restoreActiveHitIndexRef = useRef(
    options?.initialState?.activeHitIndex ?? -1
  )
  const restoreQueryRef = useRef(options?.initialState?.query ?? '')

  const onStateChangeRef = useRef(options?.onStateChange)
  onStateChangeRef.current = options?.onStateChange
  useEffect(() => {
    onStateChangeRef.current?.({ isOpen, query, activeHitIndex })
  }, [isOpen, query, activeHitIndex])

  const { authorizedAxios: axios } = useGeckoAuth()
  const { isPreviewMode } = usePipelineConfig()
  const { POWER_BACKEND_URL } = useRuntimeVariables()

  const activeHit = activeHitIndex >= 0 ? (hits[activeHitIndex] ?? null) : null

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => {
    setIsOpen(false)
    setQuery('')
    setDebouncedQuery('')
    setHits([])
    setActiveHitIndex(-1)
    if (abortRef.current) {
      abortRef.current.abort()
    }
  }, [])

  const goToNext = useCallback(() => {
    if (hits.length === 0) return
    setActiveHitIndex((prev) => (prev + 1) % hits.length)
  }, [hits.length])

  const goToPrevious = useCallback(() => {
    if (hits.length === 0) return
    setActiveHitIndex((prev) => (prev - 1 + hits.length) % hits.length)
  }, [hits.length])

  useEffect(() => {
    if (!isOpen) return
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query)
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, isOpen])

  useEffect(() => {
    if (!debouncedQuery.trim() || !isOpen) {
      setHits([])
      setActiveHitIndex(-1)
      return
    }

    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)

    fetchIntraDocSearchHits(
      axios,
      POWER_BACKEND_URL,
      documentId,
      siteId,
      debouncedQuery,
      controller.signal,
      200,
      isPreviewMode
    )
      .then((hits) => {
        if (controller.signal.aborted) return
        setHits(hits)
        const restoreIndex = restoreActiveHitIndexRef.current
        restoreActiveHitIndexRef.current = -1
        if (hits.length === 0) {
          setActiveHitIndex(-1)
        } else if (
          restoreIndex > 0 &&
          debouncedQuery === restoreQueryRef.current
        ) {
          setActiveHitIndex(Math.min(restoreIndex, hits.length - 1))
        } else {
          setActiveHitIndex(0)
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        console.error('Intra-doc search failed:', err)
        setHits([])
        setActiveHitIndex(-1)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => {
      controller.abort()
    }
  }, [
    debouncedQuery,
    documentId,
    siteId,
    isOpen,
    POWER_BACKEND_URL,
    axios,
    isPreviewMode,
  ])

  return {
    isOpen,
    query,
    hits,
    activeHitIndex,
    activeHit,
    isLoading,
    open,
    close,
    setQuery,
    goToNext,
    goToPrevious,
  }
}
